import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { PAYMENT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired | timeout
  const [bookingId, setBookingId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    const MAX_ATTEMPTS = 8;
    const INTERVAL_MS = 2000;

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        const ps = res.data?.payment_status;
        const st = res.data?.status;
        if (res.data?.booking_id) setBookingId(res.data.booking_id);
        if (ps === "paid") {
          setStatus("paid");
          return;
        }
        if (st === "expired" || ps === "failed") {
          setStatus(st === "expired" ? "expired" : "failed");
          return;
        }
      } catch (e) {
        // continue polling on transient errors
      }
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, INTERVAL_MS);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#fbfcfe]">
      <Navbar />
      <section className="pt-32 pb-20">
        <div
          data-testid={PAYMENT.successContainer}
          className="max-w-2xl mx-auto px-6 md:px-12 text-center"
        >
          {status === "polling" && (
            <div>
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#3d6fa8]" />
              <h1
                data-testid={PAYMENT.statusMessage}
                className="mt-8 font-heading text-3xl md:text-4xl font-semibold text-[#2a3b5c]"
              >
                Confirming your payment...
              </h1>
              <p className="mt-4 text-[#5c6b85]">This usually takes a few seconds.</p>
            </div>
          )}

          {status === "paid" && (
            <div>
              <div className="w-20 h-20 mx-auto rounded-full bg-[#ffe89a]/30 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-[#2a3b5c]" />
              </div>
              <h1
                data-testid={PAYMENT.statusMessage}
                className="mt-8 font-heading text-3xl md:text-5xl font-semibold text-[#2a3b5c]"
              >
                You’re all booked in.
              </h1>
              <p className="mt-5 text-[#5c6b85] text-lg leading-relaxed">
                Thank you, your session is confirmed and a receipt is on its
                way to your email. I’ll be in touch shortly with the meeting
                link and a quick pre-session note.
              </p>
              {bookingId && (
                <p className="mt-4 text-sm text-[#5c6b85]">
                  Booking reference:{" "}
                  <span className="font-mono text-[#2a3b5c]">{bookingId.slice(0, 8)}</span>
                </p>
              )}
              <Button
                data-testid={PAYMENT.returnHomeButton}
                onClick={() => navigate("/")}
                className="mt-10 rounded-full px-7 py-6 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
              >
                Back to home
              </Button>
            </div>
          )}

          {(status === "failed" || status === "expired" || status === "timeout") && (
            <div>
              <div className="w-20 h-20 mx-auto rounded-full bg-[#8ab8e8]/20 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-[#3d6fa8]" />
              </div>
              <h1
                data-testid={PAYMENT.statusMessage}
                className="mt-8 font-heading text-3xl md:text-4xl font-semibold text-[#2a3b5c]"
              >
                {status === "timeout"
                  ? "Still processing..."
                  : status === "expired"
                  ? "This session expired"
                  : "Payment didn’t go through"}
              </h1>
              <p className="mt-5 text-[#5c6b85]">
                {status === "timeout"
                  ? "Your payment is taking longer than expected. Please check your email for a receipt, or contact me."
                  : "No worries, try again, or get in touch if the problem continues."}
              </p>
              <div className="mt-10 flex justify-center gap-4">
                <Button
                  data-testid={PAYMENT.retryButton}
                  onClick={() => navigate("/book")}
                  className="rounded-full px-6 py-6 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
                >
                  Try again
                </Button>
                <Button
                  data-testid={PAYMENT.returnHomeButton}
                  onClick={() => navigate("/")}
                  variant="ghost"
                  className="rounded-full px-6 py-6"
                >
                  Back to home
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
